import { useEffect, useState } from "react";
import { useTableSort } from "@rentablo/components";
import {
  DEFAULT_METRIC_ORDER,
  InvestmentMetricKey,
  InvestmentSortField,
  isMetricKey,
  isSortField,
  isTextSortField,
  moveItem,
} from "./investmentListFields";

export const PREFERENCES_STORAGE_KEY = "investments-list-mobile-preferences";
export const SORT_SAVE_KEY = "investments-list-mobile";

export type InvestmentListPreferences = {
  metricOrder: InvestmentMetricKey[];
  showLabels: boolean;
};

export const DEFAULT_PREFERENCES: InvestmentListPreferences = {
  metricOrder: DEFAULT_METRIC_ORDER,
  showLabels: true,
};

const DEFAULT_SORT: {
  field: InvestmentSortField;
  direction: TableSortDirection;
} = { field: "name", direction: "ASC" };

const isValidMetricOrder = (value: unknown): value is InvestmentMetricKey[] =>
  Array.isArray(value) &&
  value.length === DEFAULT_METRIC_ORDER.length &&
  value.every(isMetricKey) &&
  new Set(value).size === DEFAULT_METRIC_ORDER.length;

// Stored values come from an older app version or a manual edit at worst, so
// every field falls back to its default on its own instead of failing as a whole.
export const parsePreferences = (
  raw: string | null
): InvestmentListPreferences => {
  if (!raw) return DEFAULT_PREFERENCES;

  try {
    const parsed = JSON.parse(raw);
    return {
      metricOrder: isValidMetricOrder(parsed?.metricOrder)
        ? parsed.metricOrder
        : DEFAULT_PREFERENCES.metricOrder,
      showLabels:
        typeof parsed?.showLabels === "boolean"
          ? parsed.showLabels
          : DEFAULT_PREFERENCES.showLabels,
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
};

const readPreferences = (): InvestmentListPreferences => {
  try {
    return parsePreferences(localStorage.getItem(PREFERENCES_STORAGE_KEY));
  } catch {
    return DEFAULT_PREFERENCES;
  }
};

/**
 * View state of the mobile investment list: sort field and direction, the
 * order of the value chips and whether the chips show their labels. Shared by
 * the list and the settings menu in its header, and remembered per browser.
 */
export const useInvestmentListView = () => {
  const [preferences, setPreferences] = useState(readPreferences);
  const { sortField, sortDirection, sort } = useTableSort(
    DEFAULT_SORT,
    SORT_SAVE_KEY
  );

  useEffect(() => {
    try {
      localStorage.setItem(
        PREFERENCES_STORAGE_KEY,
        JSON.stringify(preferences)
      );
    } catch {
      // Storage can be unavailable (private mode, quota); the view still works.
    }
  }, [preferences]);

  const activeField: InvestmentSortField = isSortField(sortField)
    ? sortField
    : DEFAULT_SORT.field;
  const activeDirection: TableSortDirection =
    sortDirection === "DESC" ? "DESC" : "ASC";

  return {
    sortField: activeField,
    sortDirection: activeDirection,
    isSortChanged:
      activeField !== DEFAULT_SORT.field ||
      activeDirection !== DEFAULT_SORT.direction,
    // Text fields start A to Z, values start with the largest number.
    setSortField: (field: InvestmentSortField) =>
      sort(field, isTextSortField(field) ? "ASC" : "DESC"),
    setSortDirection: (direction: TableSortDirection) =>
      sort(activeField, direction),
    setSort: (field: InvestmentSortField, direction: TableSortDirection) =>
      sort(field, direction),
    metricOrder: preferences.metricOrder,
    showLabels: preferences.showLabels,
    isPreferencesChanged:
      preferences.showLabels !== DEFAULT_PREFERENCES.showLabels ||
      preferences.metricOrder.some(
        (key, index) => key !== DEFAULT_PREFERENCES.metricOrder[index]
      ),
    reset: () => {
      sort(DEFAULT_SORT.field, DEFAULT_SORT.direction);
      setPreferences(DEFAULT_PREFERENCES);
    },
    setShowLabels: (showLabels: boolean) =>
      setPreferences((current) => ({ ...current, showLabels })),
    moveMetric: (from: number, to: number) =>
      setPreferences((current) => ({
        ...current,
        metricOrder: moveItem(current.metricOrder, from, to),
      })),
  };
};

export type InvestmentListView = ReturnType<typeof useInvestmentListView>;
